import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { AgreementStatus } from 'src/lib/fliko/helpers/user.helper';
import { ReplaceObj } from 'src/lib/fliko/interfaces/replaceMap';
import { ProtocolType } from 'src/lib/fliko/models/agreements/protocol.data';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { ParkingType } from 'src/lib/fliko/models/units/parking.data';
import { StorageType } from 'src/lib/fliko/models/units/storage.data';
import { RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { UserType } from 'src/lib/fliko/models/users/user.data';
import { LocLabel, LocLabelModifier } from '../components/clickable-row-table/models/data-table-config.models';
import { translateShortCaseToTitle } from '../models/declination-case.data';
import { LocalizationService } from '../services/localization.service';

// TODO: remove usage of LocalizationService
export class GrammarHelper {
  private static getKeyFromCase(key: string, caseShort: string): string {
    return `${key}.${translateShortCaseToTitle(caseShort)}`;
  }

  public static unitCodeTransObj(type: RentalUnitType, eType: EstateType): ReplaceObj {
    return { key: 'unit.code', replaceMap: { $type: GrammarHelper.getUnitKeyInCase(eType, type, 'D') } };
  }

  /**
   * Get unit translation key in given case
   * @param {EstateType}     estateType
   * @param {RentalUnitType} unitType
   * @param {string}         caseShort  polish case shortened, eg. 'M', 'D', 'C' etc.
   * @return {string} translation key of unit
   */
  public static getUnitKeyInCase(estateType: EstateType, unitType: RentalUnitType, caseShort: string): string {
    let key: string;
    switch (unitType) {
      case RentalUnitType.APARTMENT:
        key = estateType == EstateType.COMMUNITY ? commApartmentSingleKey : prsApartmentSingleKey;
        break;
      case RentalUnitType.STORAGE:
        key = storageSingleKey;
        break;
      case RentalUnitType.PARKING:
        key = parkingSingleKey;
        break;
    }
    return this.getKeyFromCase(key, caseShort);
  }

  /**
   * Get key modified to quantity
   * @param {string} base  base of key
   * @param {number} count quantity
   * @param {string} empty replace 0 value, if null 0 will be treated as any other number
   * @return {string}
   */
  public static getCounterKey(base: string, count: number, empty: string | null): string {
    const absolute = Math.abs(count);

    if (absolute == 1) return base;
    if (empty !== null && absolute == 0) return empty;

    const mod10 = absolute % 10;
    const mod100 = absolute % 100;

    const isLastDigit234 = 2 <= mod10 && mod10 <= 4;
    const isTeen = 11 <= mod100 && mod100 <= 19;

    return base + (isLastDigit234 && !isTeen ? 's' : 's5');
  }

  /** @deprecated use getResidentKeyInCase */
  public static getResidentInCase(
    estateType: EstateType,
    residentType: ResidentType | string,
    caseShort: string,
    ls: LocalizationService,
    plural: boolean = false
  ): string {
    return ls.translate(this.getResidentKeyInCase(estateType, residentType, caseShort, plural));
  }

  public static getResidentKeyInCase(
    estateType: EstateType,
    residentType: ResidentType | string,
    caseShort: string,
    plural: boolean = false
  ): string {
    if (residentType == ResidentType.UNASSIGNED) {
      return (plural ? 'people' : 'person') + '.unassigned';
    } else {
      let key: string;
      switch (residentType) {
        case ResidentType.LANDLORD:
        default:
          if (plural) key = estateType == EstateType.COMMUNITY ? commLandlordPluralKey : landlordPluralKey;
          else key = estateType == EstateType.COMMUNITY ? commLandlordSingleKey : landlordSingleKey;
          break;
        case ResidentType.TENANT:
          if (plural) key = tenantPluralKey;
          else key = tenantSingleKey;
          break;
      }
      return this.getKeyFromCase(key, caseShort);
    }
  }

  public static unitTypeKey(type: RentalUnitType, estateType: EstateType, plural: boolean = false) {
    let key = '';
    const suffix = plural ? 'plural' : 'nominative';
    switch (type) {
      case RentalUnitType.APARTMENT:
        if (estateType == EstateType.PRS) key = `PRSapartment.${suffix}`;
        else key = `commApartment.${suffix}`;
        break;
      case RentalUnitType.PARKING:
        key = `parking.${suffix}`;
        break;
      case RentalUnitType.STORAGE:
        key = `storage.${suffix}`;
        break;
    }
    return key;
  }

  public static unitTypeHR(type: RentalUnitType, estateType: EstateType, ls: LocalizationService, plural: boolean = false) {
    return ls.translate(GrammarHelper.unitTypeKey(type, estateType, plural));
  }

  public static mpkDisplayName(type: RentalUnitType, eType: EstateType, ls: LocalizationService) {
    return StrHlp.capitalize(ls.trReplace(this.unitCodeTransObj(type, eType)));
  }

  public static mpkDisplayLL(type: RentalUnitType, eType: EstateType): LocLabel {
    return {
      ...this.unitCodeTransObj(type, eType),
      modifier: LocLabelModifier.CAP
    };
  }

  public static userTypeHR(type: UserType | string, ls: LocalizationService): string {
    const key = 'userType.' + type;
    return ls.translate(key);
  }

  public static parkingTypeHR(type: ParkingType | null | undefined, ls: LocalizationService): string {
    return ls.translate(this.parkingTypeKey(type));
  }

  public static parkingTypeKey(type: ParkingType | null | undefined): string {
    let key = 'parkingType.';
    // prettier-ignore
    switch (type) {
      case ParkingType.GARAGE:
      case ParkingType.LIFT:
      case ParkingType.HANDICAPPED_SPOT:  key += type;          break;
      case ParkingType.OTHER:             key = 'label.otherM'; break;
      case ParkingType.PARKING_SPOT:
      default:                            key += 'default';
    }
    return key;
  }

  public static storageTypeHR(type: StorageType | null | undefined, ls: LocalizationService): string {
    return ls.translate(this.storageTypeKey(type));
  }

  public static storageTypeKey(type: StorageType | null | undefined): string {
    let key = 'storageType.';
    // prettier-ignore
    switch (type) {
      case StorageType.STORAGE_ROOM:  key += 'room';        break;
      case StorageType.LOCKER:        key += 'locker';      break;
      case StorageType.BIKE_BOX:      key += 'bikeBox';     break;
      case StorageType.TECHNICAL:     key += 'technical';   break;
      case StorageType.OTHER:         key = 'label.otherM'; break;
      default:                        key += 'default';     break;
    }
    return key;
  }

  public static protocolName(type: ProtocolType | undefined, ls: LocalizationService): string {
    let key = 'protocolType.';
    switch (type) {
      case ProtocolType.ACCEPTANCE:
      case ProtocolType.DELIVERY:
        key += type;
        break;
      case ProtocolType.BEFORE_ACCEPTANCE:
        key += 'befAcceptance';
        break;
      case ProtocolType.OTHER:
      default:
        key += 'default';
        break;
    }
    return ls.translate(key);
  }

  public static agrStatusHR(status: AgreementStatus, ls: LocalizationService, plural: boolean = false): string {
    let key = 'select.';
    switch (status) {
      case AgreementStatus.ACTIVE:
      case AgreementStatus.FUTURE:
        key += status;
        if (plural) key += 'Plural';
        break;
      case AgreementStatus.ARCHIVED:
        key += 'archival';
        if (plural) key += 'Plural';
        break;
    }
    return ls.translate(key);
  }
}

const landlordSingleKey = 'PRSlandlord';
const landlordPluralKey = landlordSingleKey + 's';
const tenantSingleKey = 'tenant';
const tenantPluralKey = tenantSingleKey + 's';

const commLandlordSingleKey = 'commLandlord';
const commLandlordPluralKey = commLandlordSingleKey + 's';

const storageSingleKey = 'storage';
const parkingSingleKey = 'parking';

const commApartmentSingleKey = 'commApartment';
const prsApartmentSingleKey = 'PRSapartment';
